import { mapTargetCTA, onDataLayerCTA } from '@/App.datalayer';
import { DatalayerPopupContentTypes } from '@/datalayer/App.datalayer.constants';
import { useImpressionPopupEvent } from '@/datalayer/hooks/useImpressionPopupEvent';
import { ContentContainer, DetailContent, ModalContent } from '@/features/shared';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { useCustomEvent } from '@/hooks/useCustomEvent';
import { ColorNames, SpacingSizes, Theme } from '@/utils/constants';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { onDatalayer, onDatalayerCloseStickyPopup } from './StickyPopup.datalayer';
import { StickyPopupProps } from './StickyPopup.interface';
import { StickyPopupTemplates } from './StickyPopup.interface.utils';
import useStyles from './StickyPopup.styles';

const CloseButton = (props: any) => {
  return (
    <IconButton aria-label="close" {...props}>
      <CloseIcon />
    </IconButton>
  );
};

const mapVariantToClassName =
  (classes: any, isBanner = false) =>
  ({ variant, props }: any) => {
    if (/column/i.test(variant)) {
      return clsx(
        classes.columnContent,
        classes.columnCenterAlign,
        isBanner && classes.columnContentPictureBanner,
        !isBanner && classes.columnContentPictureFooter,
      );
    }

    if (/^link/i.test(variant)) {
      return classes.link;
    }

    if (/^multiLink/i.test(variant)) {
      return classes.multiLink;
    }

    if (/rtc/i.test(variant)) {
      return classes.rtc;
    }

    return '';
  };

const dataLayerPosition = 'sticky_popup';

const StickyPopup = (props: Br.PropsWithContentButtons<StickyPopupProps>) => {
  const {
    containerBackgroundColor,
    nonThemeBackgroundColor,
    themeBackgroundColor = ColorNames.charcoal,
    template = StickyPopupTemplates.footer,
    detailContent = [],
    theme = Theme.dark,
    contentButtons,
    handleClose = () => {},
    showClose = true,
    titleValue,
  } = props;
  const styles = useStyles();
  const { dispatchCustomEvent } = useCustomEvent();

  const componentRef = useRef<HTMLDivElement>(null);

  const isBannerOrModal = React.useMemo(
    () => /banner/.test(template) || template === StickyPopupTemplates.modal,
    [template],
  );

  const contentContainerProps = React.useMemo(
    () => ({
      ...(isBannerOrModal && { bottomSpacing: SpacingSizes.small }),
      ...(!isBannerOrModal && { topBackgroundColor: containerBackgroundColor }),
      ...(!isBannerOrModal && { bottomBackgroundColor: containerBackgroundColor }),
    }),
    [containerBackgroundColor, isBannerOrModal],
  );

  const contentClassName = clsx(
    `${theme}-theme`,
    {
      [`bg-color-${themeBackgroundColor}`]: isBannerOrModal && !nonThemeBackgroundColor,
    },
    isBannerOrModal && styles.bannerContent,
    !isBannerOrModal && styles.footerContent,
  );

  const title = React.useMemo(() => titleValue || '', [titleValue]);

  useImpressionPopupEvent({
    titleGetter: () => title,
    contentType: DatalayerPopupContentTypes.STICKY_POPUP,
    position: dataLayerPosition,
  });

  const handleOnclose = () => {
    dispatchCustomEvent({
      target: componentRef?.current,
      eventName: 'exit-popup',
      eventInit: {
        bubbles: true,
        detail: { target: { title } },
      },
    });
    handleClose();
  };

  const StickyContent = ({ contentButtonContent }: any) => (
    <div ref={componentRef}>
      <section
        data-datalayer-title={title}
        data-datalayer-position={dataLayerPosition}
        className={contentClassName}
        ref={(el) =>
          isBannerOrModal &&
          !!nonThemeBackgroundColor &&
          el?.style.setProperty('background-color', nonThemeBackgroundColor)
        }
      >
        {contentButtonContent}
        {isBannerOrModal && showClose && (
          <CloseButton className={clsx(styles.closeButton)} onClick={handleOnclose} />
        )}
        <DetailContent
          content={detailContent}
          theme={theme}
          mapVariantToClassName={mapVariantToClassName(styles, isBannerOrModal)}
        />
      </section>
    </div>
  );

  const MappedCloseStickyPopupEvent = withGTM({
    Component: StickyContent,
    onDatalayer: onDatalayerCloseStickyPopup,
    config: {
      eventName: 'exit-popup',
      isCustom: true,
    },
  });

  const MappedStickyPopUpLink = withGTM({
    Component: MappedCloseStickyPopupEvent,
    onDatalayer,
  });

  const MappedStickyPopUpCTA = withGTM({
    Component: MappedStickyPopUpLink,
    onDatalayer: onDataLayerCTA,
    config: { mapTarget: mapTargetCTA },
  });

  if (template === StickyPopupTemplates.modal) {
    return (
      <ModalContent isOpen toggleOpen={handleOnclose} className={styles.modal} disableBackdropClick>
        <MappedStickyPopUpCTA contentButtonContent={contentButtons?.content} />
      </ModalContent>
    );
  }

  return (
    <ContentContainer className={clsx(styles.root, `${theme}-theme`)} {...contentContainerProps}>
      {!isBannerOrModal && (
        <CloseButton className={clsx(styles.closeButton)} onClick={handleOnclose} />
      )}
      <MuiContainer className={clsx({ medium: isBannerOrModal })}>
        <MappedCloseStickyPopupEvent contentButtonContent={contentButtons?.content} />
      </MuiContainer>
    </ContentContainer>
  );
};

export default StickyPopup;
